/**
 * OQTemplateManager
 *
 * CRUD interface for managing organisation-level Open Question templates.
 * Used in Admin Settings page for org admins.
 */

import { useTranslation } from 'react-i18next'
import { RefreshCw } from 'lucide-react'
import { TemplateFormModal } from './TemplateFormModal'
import { FilterBar, EmptyTemplates, TemplateGroupList } from './oq-template-manager.parts'
import { useOQTemplateManager } from './use-oq-template-manager'
import type { OQTemplateManagerState } from './use-oq-template-manager'

interface OQManagerModalsProps {
  state: OQTemplateManagerState
}

function OQManagerModals({ state }: OQManagerModalsProps): React.ReactElement {
  const { t } = useTranslation('settings')
  const {
    showCreateModal, setShowCreateModal, editingTemplate, setEditingTemplate,
    categories, creating, updating, mutationError, clearError,
    handleCreate, handleUpdate,
  } = state
  return (
    <>
      {showCreateModal && (
        <TemplateFormModal
          title={t('templates.createTemplate')}
          categories={categories}
          onClose={() => { setShowCreateModal(false); clearError() }}
          onSubmit={handleCreate}
          submitting={creating}
          error={mutationError}
        />
      )}
      {editingTemplate && (
        <TemplateFormModal
          title={t('templates.editTemplate')}
          categories={categories}
          template={editingTemplate}
          onClose={() => { setEditingTemplate(null); clearError() }}
          onSubmit={handleUpdate}
          submitting={updating}
          error={mutationError}
        />
      )}
    </>
  )
}

export function OQTemplateManager(): React.ReactElement {
  const { t } = useTranslation('settings')
  const state = useOQTemplateManager()
  const {
    templates, loading, error, refetch,
    categories, deleting,
    searchTerm, setSearchTerm, categoryFilter, setCategoryFilter,
    setShowCreateModal, setEditingTemplate,
    filteredTemplates, groupedTemplates, handleDelete,
  } = state

  if (loading)
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="h-5 w-5 animate-spin text-fuchsia-500" />
      </div>
    )

  if (error)
    return (
      <div className="py-8 text-center">
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <button onClick={() => { void refetch() }} className="mt-2 text-sm text-fuchsia-600 hover:underline dark:text-fuchsia-400">
          {t('templates.tryAgain')}
        </button>
      </div>
    )

  return (
    <div className="space-y-4">
      <FilterBar
        searchTerm={searchTerm}
        categoryFilter={categoryFilter}
        categories={categories}
        onSearchChange={setSearchTerm}
        onCategoryChange={setCategoryFilter}
        onAdd={() => { setShowCreateModal(true) }}
      />
      {filteredTemplates.length === 0 ? (
        <EmptyTemplates hasTemplates={templates.length > 0} onAdd={() => { setShowCreateModal(true) }} />
      ) : (
        <TemplateGroupList groupedTemplates={groupedTemplates} deleting={deleting} onEdit={setEditingTemplate} onDelete={handleDelete} />
      )}
      <OQManagerModals state={state} />
    </div>
  )
}
